import { useCallback, useMemo, useState } from 'react';
import { icons, Search, X, Folder } from 'lucide-react';
import {
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@clarix/ui';
import { DynamicIcon, PickerPanel } from '@clarix/ui-shared';
import { ICON_CATEGORIES } from '@clarix/utils';
import { useUpdateSidebarProject } from '@clarix/services-projects';
import type { ProjectListItem } from '@clarix/shared-types';

// ── Icon Picker Grid (inline, no popover) ────────────────────────────

interface InlineIconPickerProps {
  currentIcon: string;
  onSelect: (iconName: string) => void;
}

function InlineIconPicker({ currentIcon, onSelect }: InlineIconPickerProps) {
  const [activeCategory, setActiveCategory] = useState(0);
  const [search, setSearch] = useState('');

  const filteredIcons = useMemo(
    () =>
      search.trim()
        ? [...new Set(ICON_CATEGORIES.flatMap((c) => c.icons))].filter((name) =>
            name.toLowerCase().includes(search.toLowerCase()),
          )
        : null,
    [search],
  );

  const activeCat = ICON_CATEGORIES[activeCategory] ?? ICON_CATEGORIES[0]!;
  const displayIcons = filteredIcons ?? activeCat.icons;

  return (
    <div className="space-y-2 px-4 pb-4">
      <div className="relative">
        <Search
          className="text-muted-foreground absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2"
          strokeWidth={1.5}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search icons..."
          className="bg-surface text-foreground placeholder:text-muted-foreground h-7 w-full rounded-md border border-gray-200 pl-7 pr-7 text-lg focus:outline-none focus:ring-1 focus:ring-indigo-300"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label="Clear search"
            className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 -translate-y-1/2"
          >
            <X className="h-3 w-3" strokeWidth={1.5} />
          </button>
        )}
      </div>

      {!filteredIcons && (
        <div className="flex gap-0.5 overflow-x-auto">
          {ICON_CATEGORIES.map((cat, idx) => (
            <button
              key={cat.label}
              type="button"
              onClick={() => setActiveCategory(idx)}
              className={cn(
                'whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium transition-colors',
                activeCategory === idx
                  ? 'text-foreground bg-gray-100'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      <div className="max-h-[200px] overflow-y-auto p-1">
        {displayIcons.length === 0 ? (
          <div className="text-muted-foreground py-4 text-center text-lg">No icons found</div>
        ) : (
          <div className="grid grid-cols-8 gap-0.5">
            {displayIcons.map((iconName) => {
              const isSelected = currentIcon === iconName;
              const IconComp = icons[iconName as keyof typeof icons];
              return (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => onSelect(iconName)}
                  title={iconName}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-md transition-all',
                    isSelected
                      ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-500 ring-offset-1'
                      : 'hover:text-foreground text-gray-600 hover:scale-110 hover:bg-gray-100',
                  )}
                >
                  {IconComp ? (
                    <IconComp className="h-4 w-4" strokeWidth={1.5} />
                  ) : (
                    <Folder className="h-4 w-4" strokeWidth={1.5} />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Edit Dialog ─────────────────────────────────────────────────

interface SidebarProjectEditPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'icon' | 'color';
  project: ProjectListItem;
}

export function SidebarProjectEditPopover({
  open,
  onOpenChange,
  mode,
  project,
}: SidebarProjectEditPopoverProps) {
  const updateProject = useUpdateSidebarProject();

  const handleIconSelect = useCallback(
    (iconName: string) => {
      updateProject.mutate({ id: project.id, data: { icon: iconName } });
      onOpenChange(false);
    },
    [project.id, updateProject, onOpenChange],
  );

  const handleColorChange = useCallback(
    (color: string) => {
      updateProject.mutate({ id: project.id, data: { color } });
      onOpenChange(false);
    },
    [project.id, updateProject, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[360px]">
        <DialogHeader className="border-none">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <DynamicIcon
              name={project.icon ?? 'Folder'}
              className="h-4 w-4"
              style={project.color ? { color: project.color } : undefined}
            />
            {mode === 'icon' ? 'Change Icon' : 'Change Color'} — {project.name}
          </DialogTitle>
        </DialogHeader>

        {mode === 'icon' ? (
          <InlineIconPicker currentIcon={project.icon ?? 'Folder'} onSelect={handleIconSelect} />
        ) : (
          <PickerPanel initialColor={project.color ?? '#6366F1'} onColorChange={handleColorChange} />
        )}
      </DialogContent>
    </Dialog>
  );
}
